import api from './api';

export interface TemplateProdutoSugestao {
  id: number;
  templateSlug: string;
  ordem?: number | null;
  destaque: boolean;
  createdAt: string;
}

export interface ProdutoAfiliado {
  id: string;
  nome: string;
  descricao?: string | null;
  preco: number;
  /** Preço cheio/riscado ("de") quando o parceiro anuncia desconto */
  precoOriginal?: number | null;
  imagemUrl?: string | null;
  parceiro?: string | null;
  urlAfiliado: string;
  categoria?: string | null;
  tags: string[];
  ativo: boolean;
  destaque: boolean;
  origemDados?: string | null;
  externalId?: string | null;
  ultimaAtualizacao?: string | null;
  ultimaVerificacao?: string | null;
  estoqueDisponivel?: boolean | null;
  createdAt: string;
  updatedAt: string;
  templatesSugestoes: TemplateProdutoSugestao[];
}

export interface ListaProdutoAfiliadoResponse {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  produtos: ProdutoAfiliado[];
  categoriasDisponiveis: string[];
  tagsDisponiveis: string[];
  faixaPreco: {
    min: number | null;
    max: number | null;
  } | null;
}

export interface ProdutoAfiliadoBaseInput {
  nome: string;
  descricao?: string;
  preco: number;
  imagemUrl?: string;
  parceiro?: string;
  urlAfiliado: string;
  categoria?: string;
  tags?: string[];
  ativo?: boolean;
  destaque?: boolean;
  origemDados?: string;
  externalId?: string;
  estoqueDisponivel?: boolean;
  templateSlugs?: string[];
  ordemSugestao?: number;
}

export interface ProdutoAfiliadoFilters {
  search?: string;
  categoria?: string;
  parceiro?: string;
  /** Filtro por afiliado/fonte (origemDados): ex. "magalu,meli,amazon" */
  fontes?: string;
  /** Filtro por grupo de categoria canônico (tag cat-<slug>): ex. "beleza" */
  catGrupo?: string;
  /** Ordenação da vitrine: 'preco-asc' | 'preco-desc' | undefined (padrão curado) */
  ordem?: string;
  templateSlug?: string;
  ativo?: boolean;
  destaque?: boolean;
  tag?: string;
  minPreco?: number;
  maxPreco?: number;
  page?: number;
  pageSize?: number;
}

const montarParams = (filters: ProdutoAfiliadoFilters = {}) => {
  const params: Record<string, string> = {};

  if (filters.search) params.search = filters.search;
  if (filters.categoria) params.categoria = filters.categoria;
  if (filters.parceiro) params.parceiro = filters.parceiro;
  if (filters.fontes) params.fontes = filters.fontes;
  if (filters.catGrupo) params.catGrupo = filters.catGrupo;
  if (filters.ordem) params.ordem = filters.ordem;
  if (filters.templateSlug) params.templateSlug = filters.templateSlug;
  if (typeof filters.ativo === 'boolean') params.ativo = String(filters.ativo);
  if (typeof filters.destaque === 'boolean') params.destaque = String(filters.destaque);
  if (filters.tag) params.tag = filters.tag;
  if (typeof filters.minPreco === 'number') params.minPreco = String(filters.minPreco);
  if (typeof filters.maxPreco === 'number') params.maxPreco = String(filters.maxPreco);
  if (typeof filters.page === 'number') params.page = String(filters.page);
  if (typeof filters.pageSize === 'number') params.pageSize = String(filters.pageSize);

  return params;
};

export const listarProdutosAfiliadosAdmin = async (filters: ProdutoAfiliadoFilters = {}) => {
  const response = await api.get<ListaProdutoAfiliadoResponse>('/admin/produtos-afiliados', {
    params: montarParams(filters),
  });
  return response.data;
};

export interface ImportacaoCatalogoPayload {
  fornecedores?: Array<'magalu' | 'amazon'>;
  magalu?: {
    termos?: string[];
    limite?: number;
    categoriaPadrao?: string;
    loja?: string;
  };
  dryRun?: boolean;
}

export interface ImportacaoMagaluResultado {
  termosUtilizados: string[];
  limitePorTermo: number;
  lojaUtilizada: string;
  categoriaPadrao?: string;
  totalColetados: number;
  criados: number;
  atualizados: number;
  totalPersistidos: number;
  dryRun: boolean;
  duracaoMs: number;
}

export interface ImportacaoCatalogoResponse {
  message: string;
  fornecedores: Array<'magalu' | 'amazon'>;
  resultados: {
    magalu?: ImportacaoMagaluResultado;
    amazon?: {
      message?: string;
    };
  };
}

export const importarCatalogoProdutosAfiliados = async (
  payload: ImportacaoCatalogoPayload = {}
) => {
  const response = await api.post<ImportacaoCatalogoResponse>(
    '/admin/produtos-afiliados/importar',
    payload
  );
  return response.data;
};

export const criarProdutoAfiliado = async (payload: ProdutoAfiliadoBaseInput) => {
  const response = await api.post<{ message: string; produto: ProdutoAfiliado | null }>(
    '/admin/produtos-afiliados',
    payload
  );
  return response.data;
};

export const atualizarProdutoAfiliado = async (
  id: string,
  payload: Partial<ProdutoAfiliadoBaseInput>
) => {
  const response = await api.put<{ message: string; produto: ProdutoAfiliado | null }>(
    `/admin/produtos-afiliados/${id}`,
    payload
  );
  return response.data;
};

export const removerProdutoAfiliado = async (id: string, force: boolean = false) => {
  const response = await api.delete<{ message: string }>(`/admin/produtos-afiliados/${id}`, {
    params: force ? { force: 'true' } : undefined,
  });
  return response.data;
};

export const vincularProdutoTemplate = async (
  id: string,
  payload: { templateSlug: string; ordem?: number; destaque?: boolean }
) => {
  const response = await api.post<{ message: string }>(
    `/admin/produtos-afiliados/${id}/templates`,
    payload
  );
  return response.data;
};

export const desvincularProdutoTemplate = async (id: string, templateSlug: string) => {
  const response = await api.delete<{ message: string }>(
    `/admin/produtos-afiliados/${id}/templates/${templateSlug}`
  );
  return response.data;
};

export const listarSugestoesTemplate = async (templateSlug: string) => {
  const response = await api.get<{
    templateSlug: string;
    produtos: Array<{
      sugestaoId: number;
      ordem?: number | null;
      destaque: boolean;
      produto: ProdutoAfiliado;
    }>;
  }>(`/admin/templates/${templateSlug}/produtos-afiliados`);
  return response.data;
};

export const listarCatalogoProdutos = async (filters: ProdutoAfiliadoFilters = {}) => {
  const response = await api.get<ListaProdutoAfiliadoResponse>('/catalogo/produtos-afiliados', {
    params: montarParams(filters),
  });
  return response.data;
};

/**
 * Registra um clique em produto (Ver oferta / Comprar) — fire-and-forget.
 * Não aguarda resposta nem bloqueia o redirect pro afiliado.
 */
export const registrarCliqueProduto = (produtoId: string, origem: string): void => {
  void api.post('/catalogo/clique', { produtoId, origem }).catch(() => {});
};
